<template>
  <!--
    매장 주문접수 임시중단 카드(R-E1) — 매장 계정의 「주문 임시중단」 화면과 매장 상세(사업장 트리·매장 관리)가
    **같은 이 카드**를 쓴다. 판정·문구는 `utils/storePause` 한 곳이고, 이 카드는 시계를 읽고(경계) 요청하고 그린다.

    상태의 주인은 부모다: `pausedUntil` 을 받아 그리고, 걸기·해제 응답의 값을 `update:pausedUntil` 로 돌려준다
    (`:paused-until.sync`). 그러면 매장 상세 폼과 이 카드가 같은 한 칸을 본다.

    권한은 세션 **액션**만 본다(역할 이름 없음) — 걸기·해제 액션이 하나도 없는 세션에는 카드가 없다.
  -->
  <el-card v-if="canPause || canResume" shadow="never" class="store-pause-card">
    <div slot="header" class="store-pause-card__header">{{ $t(text.title) }}</div>

    <template v-if="state.paused">
      <el-alert class="store-pause-card__status" type="warning" :title="message" :closable="false" show-icon />
      <div v-if="canResume" class="store-pause-card__actions">
        <el-button
          type="primary"
          size="small"
          :loading="pending === RESUME_KEY"
          :disabled="pending !== null"
          @click="resume"
          >{{ $t(text.resume) }}</el-button
        >
      </div>
    </template>

    <template v-else>
      <div v-if="canPause" class="store-pause-card__actions store-pause-card__lengths">
        <el-button
          v-for="length in lengths"
          :key="length.minutes"
          size="small"
          :loading="pending === length.minutes"
          :disabled="pending !== null"
          @click="pause(length.minutes)"
          >{{ $t(length.label) }}</el-button
        >
      </div>
      <p class="store-pause-card__help">{{ $t(text.help) }}</p>
    </template>

    <!-- 서버 문장 그대로(토스트 대신 여기서 한 번) — 다음 요청이나 매장 전환이 지운다 -->
    <el-alert
      v-if="errorMessage"
      class="store-pause-card__error"
      type="error"
      :title="errorMessage"
      :closable="false"
      show-icon />
  </el-card>
</template>

<script>
import { httpErrorMessage } from '@/plugins/http/policy'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'
import { latestGuard } from '@/utils/latestGuard'
import {
  STORE_PAUSE_ACTION,
  STORE_PAUSE_LENGTHS,
  STORE_PAUSE_REQUEST_CONFIG,
  STORE_PAUSE_TEXT,
  storePauseMessage,
  storePauseRecheckIn,
  storePauseState,
  storePauseUrl,
} from '@/utils/storePause'

/** 진행 중 표시의 키 — 걸기는 그 길이(분)이고 해제는 이 값이다 */
const RESUME_KEY = 'resume'

export default {
  name: 'StorePauseCard',

  props: {
    /** 대상 매장 — 바뀌면 나가 있던 요청의 응답을 버린다 */
    storeCd: { type: [Number, String], required: true },
    /** 매장 응답의 `pausedUntil`(ISO-8601, 중단 중일 때만 값) — 부모가 소유한다 */
    pausedUntil: { type: String, default: null },
  },

  data() {
    return {
      text: STORE_PAUSE_TEXT,
      lengths: STORE_PAUSE_LENGTHS,
      RESUME_KEY,
      /** 판정의 「지금」 — 여기(경계)에서만 시계를 읽는다 */
      now: Date.now(),
      timer: null,
      /** 진행 중인 요청(길이 분 또는 [RESUME_KEY]) — 없으면 `null` */
      pending: null,
      errorMessage: '',
      /** 매장이 바뀐 뒤 도착한 앞 매장의 응답이 지금 매장의 칸을 덮지 않게(`utils/latestGuard`) */
      guard: latestGuard(),
    }
  },

  computed: {
    state() {
      return storePauseState(this.pausedUntil, this.now)
    },
    message() {
      return storePauseMessage(this.state)
    },
    canPause() {
      return checkAuthActionAccess(STORE_PAUSE_ACTION.PAUSE)
    },
    canResume() {
      return checkAuthActionAccess(STORE_PAUSE_ACTION.RESUME)
    },
  },

  watch: {
    /**
     * 값이 바뀌면 「지금」부터 다시 잰다 — 중단 아닐 때는 시계를 돌리지 않으므로 `now` 는 마지막 계산 시각에
     * 멈춰 있다. 그대로 두면 한참 뒤에 건 30분이 「50분 남음」으로 보인다.
     * (이 watcher 가 `state` 보다 먼저 선언돼 같은 회차에서 먼저 돈다 — `state` 는 새 `now` 로 계산된다.)
     */
    pausedUntil() {
      this.now = Date.now()
    },
    storeCd() {
      this.guard.invalidate()
      this.pending = null
      this.errorMessage = ''
      this.now = Date.now()
    },
    state: {
      immediate: true,
      handler() {
        this.schedule()
      },
    },
  },

  beforeDestroy() {
    clearTimeout(this.timer)
  },

  methods: {
    /** 다음 계산 예약 — 남은 분이 바뀌는 순간(마지막은 만료 순간). 중단 아니면 예약하지 않는다 */
    schedule() {
      clearTimeout(this.timer)

      const delay = storePauseRecheckIn(this.state, this.now)

      this.timer = delay === null ? null : setTimeout(this.tick, delay)
    },
    tick() {
      this.now = Date.now()
    },
    pause(minutes) {
      return this.send(minutes, () =>
        this.$axios.put(storePauseUrl(this.storeCd), { minutes }, STORE_PAUSE_REQUEST_CONFIG)
      )
    },
    resume() {
      return this.send(RESUME_KEY, () => this.$axios.delete(storePauseUrl(this.storeCd), STORE_PAUSE_REQUEST_CONFIG))
    },
    /**
     * 걸기·해제 공통 — 응답의 `pausedUntil` 이 새 상태다(해제는 `null`). 실패는 서버 문장을 카드에 남기고
     * 상태는 그대로 둔다(서버가 바꾸지 않았다).
     */
    async send(key, request) {
      if (this.pending !== null) return

      const token = this.guard.begin()

      this.pending = key
      this.errorMessage = ''

      try {
        const response = await request()

        if (!this.guard.isLatest(token)) return

        const body = (response && response.data) || {}

        this.$emit('update:pausedUntil', body.pausedUntil || null)
      } catch (error) {
        if (!this.guard.isLatest(token)) return

        this.errorMessage = httpErrorMessage(error, this.$t(STORE_PAUSE_TEXT.failed))
      } finally {
        if (this.guard.isLatest(token)) this.pending = null
      }
    },
  },
}
</script>

<style scoped>
.store-pause-card {
  margin-bottom: 12px;
}

.store-pause-card__header {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #1a1a2e);
}

.store-pause-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.store-pause-card__actions .el-button + .el-button {
  margin-left: 0;
}

.store-pause-card__actions:first-child {
  margin-top: 0;
}

/*
  길이 버튼 16개 — 4열 격자로 가지런히, 좁은 화면(`$device.mobile`, <769px)은 2열. 폭 상한은 매장 관리의 전체 화면
  다이얼로그에서 버튼이 화면 끝까지 늘어나지 않게 한다
*/
.store-pause-card__lengths {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  max-width: 560px;
}

.store-pause-card__lengths .el-button {
  width: 100%;
}

@media (max-width: 768px) {
  .store-pause-card__lengths {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.store-pause-card__help {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-regular, #4a4a68);
}

.store-pause-card__error {
  margin-top: 10px;
}
</style>
